import { ApolloClient, gql, NormalizedCacheObject, useQuery } from '@apollo/client';
import { ChartStatSeries, TimeRange } from '@amzn/search-services-cluster-insights-commons-lib/dist/common/interfaces';
import { GET_METRICS, GetMetricsData, GetMetricsVars } from './useGetMetricsQuery';
import { transformMetricSummary } from '../Utils/metricTransformers';
import { DEFAULT_FILTERS, DEFAULT_SORTING_METRIC, NON_SORTING_METRICS } from '../../../constants';
import { fetchClusterInfo } from '../../../pages/DomainDetails/hooks/useClusterInfoQuery';
import { get } from 'lodash';

export interface GetMetricsAggregationsVars {
  entityType: string;
  entityValue: string;
  startTime: number;
  endTime: number;
  metricName: string;
  aggregateBy: string;
  stat: string;
  limit: number;
  filtersJson?: any;
  sortOrder: string;
}

export interface GetMetricsAggregationsData {
  getMetricsAggregations: any;
}

export interface Filters { period: string; statistics: string; sortField: string; sortDirection: string }

const GET_METRICS_AGGREGATIONS = gql`
  query GetMetricsAggregations(
    $entityType: String,
    $entityValue: String,
    $startTime: Float,
    $endTime: Float,
    $metricName: String,
    $aggregateBy: String,
    $stat: String,
    $limit: Int,
    $filtersJson: JSON,
    $sortOrder: String
  ) {
    getMetricsAggregations(
      entityType: $entityType,
      entityValue: $entityValue,
      startTime: $startTime,
      endTime: $endTime,
      metricName: $metricName,
      aggregateBy: $aggregateBy,
      stat: $stat,
      limit: $limit,
      filtersJson: $filtersJson,
      sortOrder: $sortOrder
    )
  }
`;

export async function fetchDomainMetricsAggregations(
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  domainIdent: string,
  range: TimeRange,
  filters: Filters,
  sortingField = DEFAULT_SORTING_METRIC,
  executeFilteredApi: any
): Promise<any> {
  const { statistics, sortField, sortDirection } = filters;

  const metricName = sortField || sortingField;
  const filtersJson = JSON.stringify([{
    name: 'node_type',
    value: 'data',
  }]);
  const statToUse = NON_SORTING_METRICS.includes(statistics) ? 'avg' : statistics;
  const { data } = await client.query<GetMetricsAggregationsData, GetMetricsAggregationsVars>({
    query: GET_METRICS_AGGREGATIONS,
    variables: {
      entityType: 'DOMAINIDENT',
      entityValue: domainIdent,
      startTime: range.startTime,
      endTime: range.endTime,
      metricName,
      aggregateBy: 'node_id',
      stat: statToUse,
      limit: 1000,
      filtersJson,
      sortOrder: (sortDirection || 'ASC').toUpperCase(),
    },
    fetchPolicy: 'no-cache',
  });
  const nodesFromMetrics = data.getMetricsAggregations?.metricDataPointList?.map((item: any) => item?.name)?.filter(Boolean);
  try {
    const di = `${domainIdent.split('/')[1]}:${domainIdent.split('/')[2]}`;
    const dataNodes = await executeFilteredApi({
      variables: {
        url: `_cat/nodes?h=id,node.roles&full_id&format=json`,
        domainIdentifier: di,
        filterArguement: "",
        filterValue: "",
      },
    })
    if (!dataNodes?.data?.executeGetFilteredDataAPI?.success) {
      throw new Error(dataNodes?.data?.executeGetFilteredDataAPI?.data?.errors)
    }
    const nodesFromESApi = dataNodes
      ?.data
      ?.executeGetFilteredDataAPI
      ?.data
      ?.truncated_output
      ?.filter((node) => !nodesFromMetrics?.includes(node?.id) && node?.['node.roles']?.includes('data'))
      ?.map(node => node.id);
    return { data: [...nodesFromMetrics, ...nodesFromESApi] };
  } catch (err) {
    return { data: nodesFromMetrics };
  }
}

export async function fetchIndexesAggregations(
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  domainIdent: string,
  range: TimeRange,
  filters: Filters,
  sortingField = DEFAULT_SORTING_METRIC
): Promise<any> {
  const { statistics, sortField, sortDirection } = filters;

  const metricName = sortField || sortingField;
  const filtersJson = undefined;
  const statToUse = NON_SORTING_METRICS.includes(statistics) ? 'avg' : statistics;
  const { data } = await client.query<GetMetricsAggregationsData, GetMetricsAggregationsVars>({
    query: GET_METRICS_AGGREGATIONS,
    variables: {
      entityType: 'DOMAINIDENT',
      entityValue: domainIdent,
      startTime: range.startTime,
      endTime: range.endTime,
      metricName,
      aggregateBy: 'index_name',
      stat: statToUse,
      limit: 1000,
      filtersJson,
      sortOrder: (sortDirection || 'ASC').toUpperCase(),
    },
    fetchPolicy: 'no-cache',
  });

  return { data: data.getMetricsAggregations?.metricDataPointList?.map((item: any) => item?.name)?.filter(Boolean), period: data.getMetricsAggregations?.metadata?.period || 5 };
}

export async function fetchShardsAggregations(
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  domainIdent: string,
  range: TimeRange,
  filters: Filters,
  sortingField = 'SearchLatency'
): Promise<any> {
  const { statistics, sortField, sortDirection } = filters;
  const metricName = sortField || sortingField;
  let filtersJson = null;
  if (filters?.filterBy === 'nodeId' && filters?.filteredValue) {
    filtersJson = [{
      name: 'node_id',
      value: filters?.filteredValue,
    }]
  }
  if (filters?.filterBy === 'indexName' && filters?.filteredValue) {
    filtersJson = [{
      name: 'index_name',
      value: filters?.filteredValue,
    }]
  }
  let statToUse = NON_SORTING_METRICS.includes(statistics) ? 'avg' : statistics;
  if (filters?.filterBy === 'nodeId' || filters?.filterBy === 'indexName') {
    statToUse = filters?.statistics
  }
  const { data } = await client.query<GetMetricsAggregationsData, GetMetricsAggregationsVars>({
    query: GET_METRICS_AGGREGATIONS,
    variables: {
      entityType: 'DOMAINIDENT',
      entityValue: domainIdent,
      startTime: range.startTime,
      endTime: range.endTime,
      metricName,
      aggregateBy: 'shard_id',
      stat: statToUse,
      limit: 1000,
      filtersJson: filtersJson ? JSON.stringify(filtersJson) : undefined,
      sortOrder: (sortDirection || 'ASC').toUpperCase(),
    },
    fetchPolicy: 'no-cache',
  });

  return { data: data.getMetricsAggregations?.metricDataPointList?.map((item: any) => item?.name)?.filter(Boolean), period: data.getMetricsAggregations?.metadata?.period || 5 };
}

export const fetchResourceRelatedMetrics = async (
  client: any,
  domainIdentuple: string,
  id: string,
  range: TimeRange,
  filters = DEFAULT_FILTERS,
  metricsList: string[],
  keyMap: Record<string, string>,
  isShardMetrics: boolean = false,
): Promise<Record<string, number | string>> => {
  const results = await Promise.allSettled([
    fetchNodeMultiMetricData(client, domainIdentuple, metricsList, id, range, filters, keyMap, isShardMetrics),
  ]);
  const transformed = transformMetricSummary(results[0], metricsList, id, filters, isShardMetrics);
  return transformed;
};


export const fetchNodeMultiMetricData = async (
  client: ApolloClient<NormalizedCacheObject> | ApolloClient<object>,
  domainIdent: string,
  metricNames: string[],
  id = '',
  range: TimeRange,
  filters = DEFAULT_FILTERS,
  keyMap,
  isShardMetrics = false,
): Promise<ChartStatSeries[]> => {
  try {
    const { period = '5', statistics = 'avg' } = filters || {};
    const metricDataQueries = metricNames.map((metricName) => {
      let filters = keyMap[metricName] ? [{
        name: keyMap[metricName],
        value: id,
      }] : undefined
      if (isShardMetrics) {
        filters = [{
          name: "shard_id",
          value: id.split('#')[2],
        }, {
          name: "index_name",
          value: id.split('#')[1],
        }, {
          name: "node_id",
          value: id.split('#')[0],
        }]
      }
      return ({
        metric_name: metricName,
        stats: Array.isArray(statistics) ? statistics.map((s) => s.toUpperCase()) : [statistics.toUpperCase()],
        filters,
      })
    });
    const { data } = await client.query<GetMetricsData, GetMetricsVars>({
      query: GET_METRICS,
      variables: {
        entityType: 'DOMAINIDENT',
        entityValue: domainIdent,
        startTime: range.startTime,
        endTime: range.endTime,
        metrics: JSON.stringify(metricDataQueries),
        period: parseInt(period),
      },
      fetchPolicy: 'no-cache',
    });
    return data.getMetrics;
  } catch (error) {
    return [];
  }
};